import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SectionHeading } from '../shared/SectionHeading';

export const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqItems = [
    { questionKey: 'landing.faq.questions.realistic.question', answerKey: 'landing.faq.questions.realistic.answer' },
    { questionKey: 'landing.faq.questions.cancel.question', answerKey: 'landing.faq.questions.cancel.answer' },
    { questionKey: 'landing.faq.questions.programs.question', answerKey: 'landing.faq.questions.programs.answer' },
    { questionKey: 'landing.faq.questions.moneyBack.question', answerKey: 'landing.faq.questions.moneyBack.answer' },
    { questionKey: 'landing.faq.questions.speaking.question', answerKey: 'landing.faq.questions.speaking.answer' },
    { questionKey: 'landing.faq.questions.freeTrial.question', answerKey: 'landing.faq.questions.freeTrial.answer' },
    { questionKey: 'landing.faq.questions.devices.question', answerKey: 'landing.faq.questions.devices.answer' },
    { questionKey: 'landing.faq.questions.difference.question', answerKey: 'landing.faq.questions.difference.answer' },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="section-shell">
      <div className="mx-auto max-w-4xl px-6">
        <SectionHeading
          eyebrow={t('landing.faq.eyebrow')}
          title={`${t('landing.faq.title').split(t('landing.faq.titleHighlight'))[0]} ${t('landing.faq.titleHighlight')}`}
          subtitle={t('landing.faq.subtitle')}
        />

        <div className="mt-10 space-y-3">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className={cn(
                'rounded-2xl border bg-white transition-all duration-300',
                openIndex === index 
                  ? 'border-primary/30 shadow-surface'
                  : 'border-primary/10 hover:border-primary/25'
              )}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="flex w-full items-center justify-between p-6 text-left"
              >
                <span className="pr-4 text-lg font-semibold text-ink">
                  {t(item.questionKey)}
                </span>
                <ChevronDown 
                  className={cn(
                    'w-6 h-6 text-primary flex-shrink-0 transition-transform duration-300',
                    openIndex === index && 'rotate-180'
                  )}
                />
              </button>
              
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300',
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                )}
              >
                <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                  {t(item.answerKey)}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="surface-card mt-10 p-8 text-center">
          <HelpCircle className="mx-auto mb-4 h-10 w-10 text-primary" />
          <h3 className="text-2xl font-bold text-ink">{t('landing.faq.notFound')}</h3>
          <p className="mx-auto mb-6 mt-2 max-w-md text-slate-600">
            {t('landing.faq.notFoundText')}
          </p>
          <a
            href="mailto:support@inburgertest.nl"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-semibold text-white transition-colors hover:brightness-110"
          >
            {t('landing.faq.emailUs')}
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
